import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CommonModal from 'src/components/CommonModal';
import Radio from 'src/components/Radio/Radio';
import { PATH } from 'src/constants/path';
import { RootState } from 'src/redux/store';

import { setCreateQuoteData, customerBillingAccountId, divisionId } from '../CreateQuote/CreateQuote.slice';

interface SelectBillingAccountProps {
    handleClose: () => void;
}

const SelectBillingAccount = ({ handleClose }: SelectBillingAccountProps) => {
    const { t } = useTranslation();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const currentBillingAccountId = useSelector(customerBillingAccountId);
    const currentDivisionId = useSelector(divisionId);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get the selected division to access its billing accounts
    const selectedDivision = currentUser?.divisions?.find((div: any) => div.id === currentDivisionId);

    const billingAccounts = selectedDivision?.customerBillingAccount || [];

    const [selectedBillingAccount, setSelectedBillingAccount] = useState<any>(billingAccounts.find((acc: any) => acc.id === currentBillingAccountId) || null);

    const handleSaveBillingAccount = () => {
        if (selectedBillingAccount) {
            dispatch(
                setCreateQuoteData({
                    data: { customerBillingAccountId: selectedBillingAccount?.id },
                })
            );
            handleClose();
            navigate(PATH.CREATE_QUOTE);
        }
    };

    const getCurrencyFlag = (currency: string) => {
        switch (currency?.toUpperCase()) {
            case 'USD':
                return '🇺🇸';
            case 'CAD':
                return '🇨🇦';
            default:
                return '🏳️';
        }
    };

    return (
        <CommonModal
            title={t('QuotingHub.Select_Billing_Account')}
            handleClose={handleClose}
            size={'max-w-[540px] h-auto max-h-[576px]'}
            primaryBtnText={t('QuotingHub.Create_Quote')}
            primaryBtnOnClick={handleSaveBillingAccount}
            primaryBtnLoading={false}
            primaryBtnDisabled={!selectedBillingAccount}
            secondaryBtnText={t('Common.Back')}
            secondaryBtnOnClick={() => handleClose()}
            secondaryBtnDisabled={false}
        >
            <div className="pt-5 pb-2.5 [&>*]:px-5 space-y-5 h-full flex flex-col">
                {billingAccounts.length > 0 ? (
                    <ul className="flex flex-col space-y-2 flex-1 overflow-auto custom-scrollbar-v2 pb-2.5">
                        {billingAccounts.map((item: any, index: number) => {
                            const isSelected = selectedBillingAccount?.id === item?.id;

                            return (
                                <li
                                    className={`rounded-lg hover:bg-utilityGray100 p-3 flex gap-x-3 items-center cursor-pointer ${isSelected ? 'bg-utilityGray100' : ''}`}
                                    key={index}
                                    onClick={() => {
                                        setSelectedBillingAccount(item);
                                    }}
                                >
                                    <div className="w-8 h-8 rounded-full border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center text-2xl bg-utilityGray100">
                                        {getCurrencyFlag(item.currency)}
                                    </div>
                                    <div className="flex-1 truncate">
                                        <h6 className="text-grayLight900 text-sm font-medium flex-1">
                                            {item.currency?.toUpperCase()} Billing Account {item?.quickBookId ? `${item?.quickBookId}` : ''}
                                        </h6>
                                    </div>
                                    <Radio
                                        id={item.id}
                                        onChangeFunc={() => {
                                            setSelectedBillingAccount(item);
                                        }}
                                        checked={selectedBillingAccount?.id === item?.id}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-grayLight600 text-sm">{t('QuotingHub.No_billing_accounts_available')}</p>
                    </div>
                )}
            </div>
        </CommonModal>
    );
};

export default SelectBillingAccount;
